import { Tween } from "@westpierstudio/luma/createjs";
import { AssetSpec, SpriteAssetSpec, TextAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { SplashScreen } from "@westpierstudio/luma/game/SplashScreen";
import { DefaultLayout } from "@westpierstudio/luma/layout";
import { KSGLogo } from "components/KSGLogo";
import { Container, Sprite } from "pixi.js";
import { implementBoundedContainer, Bounds } from "utils/BoundedContainer";
import { TextFactory, TextFactoryTokens, TextOptions } from "utils/TextFactory";
import { GameServices } from "./GameServices";

type OrientationSpec = { portrait: AssetSpec, landscape: AssetSpec };

interface DoNotShowContainerSpec extends AssetSpec {
    landscape: AssetSpec,
    portrait: AssetSpec,
    splashCheckBox: SpriteAssetSpec,
    splashCheckBoxOver: SpriteAssetSpec,
    splashTick: SpriteAssetSpec,
    doNotShowText: TextAssetSpec
    textBounds: Bounds
}

interface ContinueButtonSpec extends AssetSpec {
    landscape: AssetSpec,
    portrait: AssetSpec,
    splashContinue: SpriteAssetSpec,
    splashContinueOver: SpriteAssetSpec,
    continueText: TextAssetSpec
    textBounds: Bounds
}

interface SplashScreenSpec extends AssetSpec {
    splashBackground: SpriteAssetSpec,
    splashBackgroundPos: OrientationSpec;
    backgroundMinScale: { portrait: number, landscape: number }
    foregroundContainer: AssetSpec,
    doNotShowContainer: DoNotShowContainerSpec,
    continueButton: ContinueButtonSpec,
    potLogoPort: SpriteAssetSpec
    potLogoLand: SpriteAssetSpec
    potCharacters: SpriteAssetSpec & OrientationSpec
    messageContainer: OrientationSpec
    textOptions: TextOptions
    tokens: TextFactoryTokens
    messages: string[]
}

export class PrinceOfThievesSplashScreen extends SplashScreen {
    private spec!: SplashScreenSpec;
    private backgroundContainer!: Container;
    private background!: Sprite;
    private foregroundContainer!: Container;
    private ksgLogo!: KSGLogo;
    private doNotShowContainer!: Container;
    private doNotShowTick!: Sprite;
    private doNotShowBox!: { normal: Sprite, over: Sprite };
    private continueButton!: Container;
    private continueSprites!: { normal: Sprite, over: Sprite };
    private potLogoPort!: Sprite;
    private potLogoLand!: Sprite;
    private potCharacters!: Sprite;
    private messageContainer!: Container;
    private messages: Container[] = [];
    private textFactory!: TextFactory;

    private messageIndex = 0;

    constructor(protected override services: GameServices) {
        super(services);
    }

    override setup() {

        if (!this._hasSetup) {

            const { factory, resources, splashscreenLayout } = this.services;

            const spec = resources.getSpec(`splashscreen`) as SplashScreenSpec;

            this.setupBackground(spec.splashBackground);

            this.foregroundContainer = factory.createContainer(spec.foregroundContainer, this);

            this.ksgLogo = this.addChild(new KSGLogo(this.services, this.services.splashscreenLayout));
            this.on(`added`, () => this.ksgLogo.resize());

            this.setupDoNotShowContainer(spec.doNotShowContainer);
            this.setupContinueButton(spec.continueButton);
            this.potCharacters = factory.createSprite(spec.potCharacters, this.foregroundContainer);
            this.potLogoPort = factory.createSprite(spec.potLogoPort, this.foregroundContainer);
            this.potLogoLand = factory.createSprite(spec.potLogoLand, this.foregroundContainer);
            this.setupMessages(spec);

            this.spec = spec;

            this.resize();
        }
    }

    private setupBackground(spec: SpriteAssetSpec): void {

        const { factory, splashscreenLayout } = this.services;

        this.backgroundContainer = factory.createContainer({}, this);
        this.background = factory.createSprite(spec, this.backgroundContainer);

        this.resize();
        splashscreenLayout.onResize.add(() => this.resize());
    }

    private setupDoNotShowContainer(spec: DoNotShowContainerSpec): void {

        const { factory, resources } = this.services;

        this.doNotShowContainer = factory.createContainer({ anchor: 0.5 }, this.foregroundContainer);

        this.doNotShowBox = {
            normal: factory.createSprite(spec.splashCheckBox, this.doNotShowContainer),
            over: factory.createSprite(spec.splashCheckBoxOver, this.doNotShowContainer)
        };

        this.doNotShowTick = factory.createSprite(spec.splashTick, this.doNotShowContainer);
        const text = factory.createText(resources.getMessage(`splashDoNotShow`), spec.doNotShowText, this.doNotShowContainer);
        implementBoundedContainer(text, spec.textBounds);

        this.doNotShowContainer.interactive = this.doNotShowContainer.buttonMode = true;

        this.doNotShowContainer
            .addListener(`click`, this.onDoNotShowClick, this)
            .addListener(`tap`, this.onDoNotShowClick, this)
            .addListener(`mouseover`, this.showDoNotShowOver, this)
            .addListener(`pointerover`, this.showDoNotShowOver, this)
            .addListener(`mouseout`, this.hideDoNotShowOver, this)
            .addListener(`pointerout`, this.hideDoNotShowOver, this)
            .addListener(`mousedown`, this.hideDoNotShowOver, this)
            .addListener(`pointerdown`, this.hideDoNotShowOver, this);
    }

    private setupMessages(spec: SplashScreenSpec) {
        const { factory, splashscreenLayout } = this.services;
        const msgContainer = this.messageContainer = factory.createContainer(spec.messageContainer[splashscreenLayout.orientation], this.foregroundContainer);
        this.textFactory = new TextFactory(this.services, `pink`, spec.tokens);
        for (const messageKey of spec.messages) {
            let msg;
            this.messages.push(msg = this.textFactory.createTextFromMessageKey(messageKey, msgContainer, spec.textOptions, { visible: false }));
        }
        const msgDuration = this.services.resources.getSetting(`gameMessageDuration`).splashScreenMessageMS;
        this.messages[this.messageIndex].visible = true;
        Tween.get(msgContainer)
            .wait(msgDuration)
            .call(() => {
                this.messages[this.messageIndex].visible = false;
                this.messageIndex = (this.messageIndex + 1) % this.messages.length;
                this.messages[this.messageIndex].visible = true;
            }).loop = -1;
    }

    override onContinueClick() {

        this.continueButton.interactive = false;
        this.continueButton.visible = false;
        this.doNotShowContainer.visible = false;
        Tween.removeTweens(this);
        this.onSplashScreenContinue(this.doNotShowTick.visible);
    }

    override setProgress(_progress: number): void {
    }

    override hideLoadingBar(): void {
    }

    private onDoNotShowClick(): void {
        this.doNotShowTick.visible = !this.doNotShowTick.visible;
    }

    private showDoNotShowOver(): void {
        this.doNotShowBox.over.visible = true;
    }

    private hideDoNotShowOver(): void {
        this.doNotShowBox.over.visible = false;
    }

    private setupContinueButton(spec: ContinueButtonSpec): void {

        const { factory, resources } = this.services;

        this.continueButton = factory.createContainer({ anchor: 0.5 }, this.foregroundContainer);

        this.continueSprites = {
            normal: factory.createSprite(spec.splashContinue, this.continueButton),
            over: factory.createSprite(spec.splashContinueOver, this.continueButton)
        };

        const text = factory.createText(resources.getMessage(`splashContinue`), spec.continueText, this.continueButton);
        implementBoundedContainer(text, spec.textBounds);

        this.continueButton.interactive = this.continueButton.buttonMode = true;

        this.continueButton
            .addListener(`click`, this.onContinueClick, this)
            .addListener(`tap`, this.onContinueClick, this)
            .addListener(`mouseover`, this.showContinueOver, this)
            .addListener(`pointerover`, this.showContinueOver, this)
            .addListener(`mouseout`, this.hideContinueOver, this)
            .addListener(`pointerout`, this.hideContinueOver, this)
            .addListener(`mousedown`, this.hideContinueOver, this)
            .addListener(`pointerdown`, this.hideContinueOver, this);
    }

    private showContinueOver(): void {
        this.continueSprites.over.visible = true;
    }

    private hideContinueOver(): void {
        this.continueSprites.over.visible = false;
    }

    private resize(): void {

        const layout = this.services.splashscreenLayout as DefaultLayout;

        const bounds = layout.getScreenBoundaryInBucketSpace();
        const { width, height, left, top } = bounds;

        if (width / this.background.width > height / this.background.height) {
            this.background.width = width;
            this.background.scale.y = this.background.scale.x;
        } else {
            this.background.height = height;
            this.background.scale.x = this.background.scale.y;
        }

        if (this.spec) {

            const { factory } = this.services;
            const { orientation } = layout;

            this.ksgLogo.resize();

            const minScale = this.spec.backgroundMinScale[orientation];
            if (this.background.scale.x < minScale) {
                this.background.scale.x = this.background.scale.y = minScale;
            }
            factory.applySpec(this.background, this.spec.splashBackgroundPos[orientation]);
            factory.applySpec(this.messageContainer, this.spec.messageContainer[orientation]);
            factory.applySpec(this.doNotShowContainer, this.spec.doNotShowContainer[orientation]);
            factory.applySpec(this.continueButton, this.spec.continueButton[orientation]);
            factory.applySpec(this.potCharacters, this.spec.potCharacters[orientation]);

            const isLandscape = layout.orientation === `landscape`;
            this.potLogoLand.visible = isLandscape;
            this.potLogoPort.visible = !isLandscape;
        }
    }
}